import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';
import { useUi } from '../context/UiContext';
import ScoreDisplay from '../components/ScoreDisplay';

const CHECK_GROUPS = [
  { title: 'Contactability', max: 20, items: ['phoneValid', 'phoneReachable'] },
  { title: 'Target Fit', max: 30, items: ['location', 'customerType', 'requirement'] },
  { title: 'Purchase Intent', max: 30, items: ['actualRequirement', 'budgetTimeline', 'showsInterest'] },
  { title: 'Sales Progression', max: 20, items: ['relevantQuestions', 'appointment', 'opportunity', 'converted'] },
];

const INFO_FIELDS = [
  ['Full Name', 'full_name'],
  ['Phone', 'phone'],
  ['Email', 'email'],
  ['Location', 'location'],
  ['Service / Product', 'service_required'],
  ['Requirement', 'description'],
  ['Budget', 'budget'],
  ['Timeline', 'expected_timeline'],
  ['Preferred Date', 'preferred_date'],
  ['Additional Message', 'message'],
];

export default function LeadDetail() {
  const { brandId, leadId } = useParams();
  const navigate = useNavigate();
  const { showToast, confirmAction } = useUi();
  const [lead, setLead] = useState(null);

  const load = () => {
    api.getLead(leadId).then(setLead).catch(() => navigate(`/lead-quality-score/brands/${brandId}`));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [leadId]);

  const deleteLead = async () => {
    const ok = await confirmAction(
      'Delete Lead',
      `Are you sure you want to delete the lead for "${lead?.full_name}"? This cannot be undone.`
    );
    if (!ok) return;
    await api.deleteLead(leadId);
    showToast('Lead deleted.', 'info');
    navigate(`/lead-quality-score/brands/${brandId}`);
  };

  if (!lead) return null;

  const { score } = lead;
  const cls = score.classification;
  const mktPct = (score.marketing_score / 80) * 100;
  const salePct = (score.sales_score / 20) * 100;

  return (
    <div className="container page-content">
      <div className="page-header">
        <div className="page-header-top">
          <div>
            <button className="btn btn-ghost btn-sm mb-16" onClick={() => navigate(`/lead-quality-score/brands/${brandId}`)}>
              <BackIcon /> Back to Brand
            </button>
            <div className="flex items-center gap-12 mb-8">
              <span className={`badge badge-${cls.cls}`}>{cls.label}</span>
            </div>
            <h2>{lead.full_name}</h2>
            <div className="flex items-center gap-8 mt-6">
              <span style={{ color: 'var(--color-ink-faint)', fontSize: '0.875rem' }}>{lead.service_required || '—'}</span>
              <span style={{ color: 'var(--color-border-strong)' }}>•</span>
              <span style={{ color: 'var(--color-ink-faint)', fontSize: '0.875rem' }}>{lead.location || '—'}</span>
            </div>
          </div>
          <div className="page-header-actions">
            <button className="btn btn-danger btn-sm" onClick={deleteLead}>Delete Lead</button>
            <button className="btn btn-secondary" onClick={() => navigate(`/lead-quality-score/brands/${brandId}/leads/${leadId}/edit`)}>
              <EditIcon /> Edit Lead
            </button>
          </div>
        </div>
      </div>

      <div className="lead-detail-layout">
        <div>
          <div className="card mb-24">
            <div className="card-header">
              <h4 style={{ fontSize: '0.82rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--color-ink-faint)' }}>
                Lead Information
              </h4>
            </div>
            <div className="card-body">
              {INFO_FIELDS.map(([label, key]) => (
                <div className="info-row" key={key}>
                  <span className="info-row-label">{label}</span>
                  <span className="info-row-value">{lead[key] || '—'}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div>
          <div className="score-card">
            <div className="score-card-top">
              <ScoreDisplay score={score.total_score} max={100} />
              <div className="score-card-detail">
                <span className={`score-classification ${cls.cls}`}>{cls.label}</span>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-cream-faint)', marginBottom: 4 }}>{cls.desc}</div>
                <SubScoreRow label="Marketing Score" earned={score.marketing_score} max={80} pct={mktPct} />
                <SubScoreRow label="Sales Progression" earned={score.sales_score} max={20} pct={salePct} />
              </div>
            </div>

            {CHECK_GROUPS.map((group) => {
              const groupTotal = group.items.reduce((sum, key) => sum + (score.checks[key]?.earned || 0), 0);
              return (
                <div className="breakdown-section" key={group.title}>
                  <div className="breakdown-header">
                    <span className="breakdown-title">{group.title}</span>
                    <span className="breakdown-total">{groupTotal}/{group.max}</span>
                  </div>
                  {group.items.map((key) => {
                    const c = score.checks[key];
                    if (!c) return null;
                    const icons = { satisfied: '✓', partial: '~', failed: '✕', pending: '○' };
                    return (
                      <div className="check-item" key={key}>
                        <span className={`check-icon ${c.status}`}>{icons[c.status] || '○'}</span>
                        <span className="check-name">{c.label}</span>
                        <span className="check-score">{c.earned}/{c.max}</span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function SubScoreRow({ label, earned, max, pct }) {
  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-cream-faint)' }}>{label}</span>
        <span style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--color-cream)' }}>{earned} / {max}</span>
      </div>
      <div className="sub-score-bar" style={{ background: 'var(--color-cream-line)' }}>
        <div className="sub-score-fill" style={{ width: `${pct}%`, background: scoreColor(pct) }} />
      </div>
    </div>
  );
}

function scoreColor(pct) {
  if (pct >= 80) return 'var(--green)';
  if (pct >= 60) return 'var(--yellow)';
  if (pct >= 40) return 'var(--orange)';
  return 'var(--red)';
}

function BackIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}
